import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Laptop, Loader2, ShieldCheck, ShieldOff, Clock, Cpu, HardDrive,
  Fingerprint, History, AlertCircle,
} from 'lucide-react';
import {
  CHECK_LABELS, CHECK_ORDER, CheckStatusPill, StatePill, PostureBar,
  DEVICE_STATUS_META, relativeTime, observedText,
} from './deviceUi';
import { formatDateTime } from '../../utils/dateFormatter';

const API = '/api';

const IdRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-2.5 py-1.5" data-testid={testId}>
    <Icon className="w-3.5 h-3.5 text-black/35 dark:text-white/35 shrink-0" />
    <span className="text-[12px] text-black/45 dark:text-white/45 w-32 shrink-0">{label}</span>
    <span className="text-[13px] text-[#0A0A0A] dark:text-[#F5F5F5] font-medium truncate">{value || '—'}</span>
  </div>
);

export default function DeviceDetail() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/devices/${deviceId}`),
        axios.get(`${API}/devices/${deviceId}/snapshots`),
      ]);
      setDevice(dRes.data.device);
      setSnapshots(sRes.data.snapshots || []);
    } catch (e) {
      toast.error('Could not load device');
      navigate('/devices');
    } finally {
      setLoading(false);
    }
  }, [deviceId, navigate]);

  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    setRevoking(true);
    try {
      await axios.post(`${API}/devices/${deviceId}/revoke`);
      toast.success('Device revoked');
      setRevokeOpen(false);
      load();
    } catch (e) {
      toast.error('Failed to revoke device');
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-20 flex items-center justify-center text-black/40 dark:text-white/40">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading device…
        </div>
      </div>
    );
  }
  if (!device) return null;

  const latest = device.latest_posture || {};
  const latestChecks = latest.checks || [];
  const checksByKey = {};
  latestChecks.forEach((c) => { checksByKey[c.key] = c; });
  const connected = device.has_active_credential && device.status === 'active';

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="device-detail-page">
        <button onClick={() => navigate('/devices')} data-testid="back-to-devices"
          className="inline-flex items-center gap-1.5 text-[13px] text-black/50 dark:text-white/50 hover:text-[#0F62FE] dark:hover:text-[#5C97FF] mb-4 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Devices
        </button>

        {/* Header row */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#0F62FE]/10 flex items-center justify-center">
              <Laptop className="w-5.5 h-5.5 text-[#0F62FE] dark:text-[#5C97FF]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{device.hostname || 'Unknown device'}</h1>
              <div className="flex items-center gap-2 mt-1">
                <StatePill meta={DEVICE_STATUS_META[device.status] || DEVICE_STATUS_META.inactive} testId="detail-status" />
                <span className="text-[12px] text-black/45 dark:text-white/45">
                  {connected ? 'Connected' : 'No active credential'} · last check-in {relativeTime(device.last_seen_at)}
                </span>
              </div>
            </div>
          </div>
          {device.status !== 'retired' && (
            <Button variant="outline" data-testid="revoke-device-btn" onClick={() => setRevokeOpen(true)}
              className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/25 dark:text-red-400 dark:hover:bg-red-500/10">
              <ShieldOff className="w-4 h-4 mr-1.5" /> Revoke device
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Identity */}
          <Card className="border-black/10 dark:border-white/10 lg:col-span-1">
            <CardContent className="p-4">
              <h2 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2 mb-2">
                <Fingerprint className="w-4 h-4 text-black/50 dark:text-white/50" /> Device identity
              </h2>
              <div className="divide-y divide-black/5 dark:divide-white/5">
                <IdRow icon={Cpu} label="Model" value={device.model} testId="id-model" />
                <IdRow icon={Laptop} label="OS version" value={`macOS ${device.os_version || ''}`} testId="id-os" />
                <IdRow icon={HardDrive} label="Serial (last 4)" value={device.serial_last4 ? `•••• ${device.serial_last4}` : '—'} testId="id-serial" />
                <IdRow icon={ShieldCheck} label="Assurance" value={device.assurance_level} testId="id-assurance" />
                <IdRow icon={Clock} label="Enrolled" value={formatDateTime(device.enrolled_at)} testId="id-enrolled" />
                <IdRow icon={Clock} label="Last check-in" value={formatDateTime(device.last_seen_at)} testId="id-lastseen" />
                <IdRow icon={History} label="Agent" value={device.agent_version} testId="id-agent" />
              </div>
            </CardContent>
          </Card>

          {/* Latest posture */}
          <Card className="border-black/10 dark:border-white/10 lg:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-black/50 dark:text-white/50" /> Latest posture
                </h2>
                <PostureBar summary={latest.summary} />
              </div>
              {latestChecks.length === 0 ? (
                <p className="text-[13px] text-black/45 dark:text-white/45 py-2">No posture reported yet.</p>
              ) : (
                <div className="space-y-2" data-testid="latest-checks">
                  {CHECK_ORDER.filter((k) => checksByKey[k]).map((k) => {
                    const c = checksByKey[k];
                    return (
                      <div key={k} className="flex items-center gap-3 rounded-lg border border-black/8 dark:border-white/8 px-3 py-2.5" data-testid={`check-${k}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">{CHECK_LABELS[k] || k}</div>
                          {observedText(c.observed) && (
                            <div className="text-[11px] text-black/45 dark:text-white/45 font-mono mt-0.5">{observedText(c.observed)}</div>
                          )}
                        </div>
                        <CheckStatusPill status={c.status} testId={`check-status-${k}`} />
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-black/40 dark:text-white/40 pt-1">
                    Collected {formatDateTime(latest.collected_at)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <Card className="border-black/10 dark:border-white/10 mt-4">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-black/50 dark:text-white/50" /> Check-in history
              <span className="text-[11px] font-normal text-black/40 dark:text-white/40">({snapshots.length})</span>
            </h2>
            {snapshots.length === 0 ? (
              <p className="text-[13px] text-black/45 dark:text-white/45">No history yet.</p>
            ) : (
              <div className="space-y-0" data-testid="history-list">
                {snapshots.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 border-b border-black/5 dark:border-white/5 last:border-0" data-testid={`snapshot-${i}`}>
                    <div className="w-2 h-2 rounded-full bg-[#0F62FE]/60 shrink-0" />
                    <span className="text-[12px] text-black/60 dark:text-white/60 w-44 shrink-0">{formatDateTime(s.received_at)}</span>
                    <PostureBar summary={s.summary} />
                    <span className="text-[11px] text-black/35 dark:text-white/35 ml-auto">{relativeTime(s.received_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent data-testid="revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" /> Revoke this device?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This disables the device's credential so it can no longer submit posture. The device record and
              its history are kept. The user will need a new enrollment code to reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} disabled={revoking}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-revoke-btn">
              {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
